import type { ContentPage } from "../types";

// Numbers come from the app itself, so they stay true: minimum, advice and
// maximum from lib/train/clips.ts, times from lib/train/eta.ts (median and
// 80th percentile of 2,103 voices, Sep-Oct 2026). Re-check both when either
// file changes.
const page: ContentPage = {
  section: "blog",
  slug: "how-to-train-an-ai-voice-model",
  title: "How to Train Your Own AI Voice Model (2026 Guide)",
  metaDescription:
    "Train an AI voice model of your own voice in under an hour: how much audio you need, how to record it, the settings that matter and how long training takes.",
  h1: "How to train your own AI voice model",
  kind: "Guide",
  stats: [
    { value: "3 min", label: "of voice is the minimum; 6+ sounds better" },
    { value: "~25 min", label: "to train on 10 minutes of audio" },
    { value: "0", label: "installs or graphics cards needed" },
  ],
  answer:
    "To train an AI voice model, record or upload at least 3 minutes of clean singing or speech in one voice (6 to 10 minutes sounds better), let Jammable cut the silence, name the voice and start training. A 10-minute dataset is usually ready in about 25 minutes, and you can then use the voice for covers and text-to-speech.",
  published: "2026-10-17",
  updated: "2026-10-17",
  blocks: [
    {
      type: "md",
      text: `## What you need

- **3 to 60 minutes of one voice.** 3 minutes is the minimum Jammable accepts; voices usually sound better with 6 minutes or more. Only singing or speech counts: silence is cut automatically and a file added twice only counts once.
- **Clean recordings.** One voice at a time, no backing vocals, no crowd, no music behind it, in a quiet room without echo.
- **Range.** Different notes, words and energy levels. A voice trained only on quiet speech will struggle to sing loudly.
- **MP3, WAV or M4A files**, or just your microphone: you can record straight into the page.

Use recordings of your own voice, or of someone who has agreed to it.`,
    },
    {
      type: "steps",
      heading: "Step by step",
      items: [
        {
          title: "Open Train and add your voice",
          body: "Go to [Train](/train) and upload files, or press Record and sing or talk. Record as many takes as you like.",
        },
        {
          title: "Check what gets kept",
          body: "Each file shows how much voice is left after the silence is removed. Trim anything that isn't the voice you want, like a cough, a second person or a noisy intro.",
        },
        {
          title: "Name it and choose who can use it",
          body: "Add a name and a picture, then pick *Private, only me* or *Public in the library*.",
        },
        {
          title: "Clean up the samples if needed",
          body: "Turn on *Remove instrumentals* if there is music under the vocals, and *Remove reverb* if the room echoes. Skip both for dry, clean recordings.",
        },
        {
          title: "Start training",
          body: "It runs on Jammable's servers, so you can close the page. You get an email when the voice is ready.",
        },
        {
          title: "Use it",
          body: "Your voice appears in your [Library](/library). Pick it on the cover maker or for text-to-speech like any other voice.",
        },
      ],
    },
    {
      type: "table",
      heading: "How long training takes",
      table: {
        caption:
          "How long training takes, by how much audio the voice learns from (2,103 voices trained Sep to Oct 2026)",
        columns: [
          "Audio after silence is cut",
          "Usually ready in",
          "Ready within (4 in 5 voices)",
        ],
        rows: [
          ["Up to 10 minutes", "about 25 minutes", "about 30 minutes"],
          ["10 to 20 minutes", "about 35 minutes", "about 45 minutes"],
          ["20 to 30 minutes", "about 55 minutes", "about 1 hour 15 minutes"],
          [
            "30 to 45 minutes",
            "about 1 hour 10 minutes",
            "about 1 hour 35 minutes",
          ],
          [
            "45 to 60 minutes",
            "about 1 hour 45 minutes",
            "about 2 hours 25 minutes",
          ],
        ],
      },
    },
    {
      type: "md",
      text: `## Tips for a better voice

- **Quality beats quantity.** 8 clean minutes train a better voice than 40 minutes recorded in a noisy room.
- **Record the way you will use it.** If the voice is for singing, give it singing, across your low and high notes. For text-to-speech, read varied sentences out loud.
- **Keep the microphone in one place.** The same distance and the same room for every take keep the tone consistent.
- **Don't stack effects.** Autotune, heavy compression and reverb get learned as part of the voice.
- **Retrain rather than patch.** If the result sounds thin or robotic, add more varied audio and train again instead of tweaking the cover settings.

## Training on your phone or computer

Training happens in the browser on any device, so there is nothing to install and no graphics card needed. People who run RVC on their own computer can also [upload an existing RVC model](/library) (a .pth file plus its .index file) instead of training a new one.`,
    },
    {
      type: "cta",
      href: "/train",
      label: "Train your voice",
      text: "Add a few minutes of your voice and Jammable does the rest.",
    },
  ],
  faqs: [
    {
      q: "How much audio do I need to train an AI voice?",
      a: "At least 3 minutes of singing or speech after silence is removed, and up to 60 minutes. Voices usually sound better with 6 to 10 minutes of clean, varied audio.",
    },
    {
      q: "How long does it take to train an AI voice model?",
      a: "It depends on the amount of audio. On Jammable, a voice trained on up to 10 minutes is usually ready in about 25 minutes, and one trained on a full hour in about 1 hour 45 minutes. You can close the page while it trains.",
    },
    {
      q: "Can I train a voice model for free?",
      a: "Training needs a plan or a training pass. Training is included with the Creator and Power plans, and training passes can be bought on their own. See the pricing page for current prices.",
    },
    {
      q: "Can I keep my AI voice private?",
      a: "Yes. Choose Private when you set it up and only you can see and use the voice. You can also share it publicly in the voice library.",
    },
    {
      q: "What file types can I upload?",
      a: "MP3, WAV and M4A. You can also record directly from your microphone on the training page.",
    },
    {
      q: "What kind of AI model does Jammable train?",
      a: "Jammable trains RVC (Retrieval-based Voice Conversion) models, the same kind of model the wider AI cover community uses. You can also upload an RVC model you trained elsewhere.",
    },
  ],
  related: [
    { label: "Train your own voice", href: "/train" },
    { label: "Browse 60,000+ AI voices", href: "/models" },
    {
      label: "Best AI cover generators",
      href: "/compare/best-ai-cover-generators",
    },
    { label: "Pricing", href: "/pricing" },
  ],
};

export default page;
